import React from 'react';

const AdsReportStats = ({ kpi = {}, loading = false }) => {
  const moneyFormatIndia = (amount) => {
    if (!amount) return '₹0';
    const num = parseFloat(amount);
    if (isNaN(num)) return '₹0';
    return `₹${num.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    })}`;
  };

  const widgetBoxStyle = {
    minHeight: '78px',
    height: '100%',
    padding: '10px 16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: 0,
    borderRadius: '6px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  };

  const titleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '0.2px',
    display: 'block',
    textAlign: 'left',
    width: '100%',
    lineHeight: '1.2',
  };

  const valueStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: '1.1',
    display: 'block',
    textAlign: 'right',
    width: '100%',
    marginTop: '4px',
  };

  const subtextStyle = {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.85)',
    display: 'block',
    textAlign: 'right',
    width: '100%',
    marginTop: '2px',
  };

  const colClass = "col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-3";

  if (loading) {
    return (
      <div className="row mb-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`stat-skeleton-${i}`} className={colClass}>
            <div className="widget widget-stats bg-light inverse-mode" style={widgetBoxStyle}>
              <div style={{ textAlign: 'left', width: '100%' }}>
                <div className="skeleton-text" style={{ width: '90px', height: '14px' }}></div>
              </div>
              <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
                <div className="skeleton-text" style={{ width: '60px', height: '22px', float: 'right' }}></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const {
    totalCost = 0,
    totalLeads = 0,
    avgCPL = 0,
    totalLikely = 0,
    totalSales = 0,
    totalRevenue = 0,
    avgCPS = 0,
    roas = 0,
    conversionRate = 0,
    totalRecords = 0,
  } = kpi;

  return (
    <div className="row mb-3">
      {/* 1. Total Ad Spend (Royal Navy Blue) */}
      <div className={colClass}>
        <div
          className="widget widget-stats inverse-mode"
          style={{ ...widgetBoxStyle, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}
        >
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Total Ad Spend</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{moneyFormatIndia(totalCost)}</span>
            <span style={subtextStyle}>{Number(totalRecords || 0).toLocaleString('en-IN')} ad records</span>
          </div>
        </div>
      </div>

      {/* 2. Total Leads (Vibrant Purple) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-purple inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Total Leads</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{Number(totalLeads || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
            <span style={subtextStyle}>Acquired leads</span>
          </div>
        </div>
      </div>

      {/* 3. Cost Per Lead (Warm Coral Orange) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-orange inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Cost Per Lead (CPL)</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{moneyFormatIndia(avgCPL)}</span>
            <span style={subtextStyle}>Avg cost / lead</span>
          </div>
        </div>
      </div>

      {/* 4. Likely Leads (Vibrant Red) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-red inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Likely Leads</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{Number(totalLikely || 0).toLocaleString('en-IN')}</span>
            <span style={subtextStyle}>High-intent leads</span>
          </div>
        </div>
      </div>

      {/* 5. Total Sales (Vibrant Emerald Green) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-green inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Total Sales</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{Number(totalSales || 0).toLocaleString('en-IN')}</span>
            <span style={subtextStyle}>{conversionRate}% conversion</span>
          </div>
        </div>
      </div>

      {/* 6. Total Revenue (Dark Blue Gradient) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-blue-dark inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Total Revenue</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{moneyFormatIndia(totalRevenue)}</span>
            <span style={subtextStyle}>Campaign revenue</span>
          </div>
        </div>
      </div>

      {/* 7. Cost Per Sale (Coral / Orange Gradient) */}
      <div className={colClass}>
        <div className="widget widget-stats bg-gradient-orange inverse-mode" style={widgetBoxStyle}>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>Cost Per Sale (CPS)</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{totalSales > 0 ? moneyFormatIndia(avgCPS) : '₹0'}</span>
            <span style={subtextStyle}>Acquisition cost / sale</span>
          </div>
        </div>
      </div>

      {/* 8. ROAS / ROI (Cyan/Teal Gradient) */}
      <div className={colClass}>
        <div
          className="widget widget-stats inverse-mode"
          style={{ ...widgetBoxStyle, background: 'linear-gradient(135deg, #0d9488 0%, #06b6d4 100%)' }}
        >
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={titleStyle}>ROAS / Net ROI</span>
          </div>
          <div style={{ textAlign: 'right', width: '100%' }}>
            <span style={valueStyle}>{roas}x</span>
            <span style={subtextStyle}>
              {totalCost > 0 ? `${(((totalRevenue - totalCost) / totalCost) * 100).toFixed(1)}% Net ROI` : '0% Net ROI'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdsReportStats;
